
import React, { useState, useEffect } from "react";
import { requestService } from "src/api/request/requestService";
import HistoryGrid from "src/components/shared-components/history-grid/HistoryGrid";

interface HistoryAreaProps {
  rowID: string;
  onHistoryServiceToggle: (serviceId: number | string, originalData?: any, associatedHistoryId?: number | string) => void;
  selectedHistoryServices: (number | string)[];
  setSelectedHistoryServices: React.Dispatch<React.SetStateAction<(number | string)[]>>;
  onHistoryParentToggle?: (parentId: number | string, children?: any[]) => void;
  searchText?: string;
}

const HistoryArea: React.FC<HistoryAreaProps> = ({
  rowID,
  onHistoryServiceToggle,
  selectedHistoryServices,
  setSelectedHistoryServices,
  onHistoryParentToggle,
  searchText
}) => {
  const [historyApiData, setHistoryApiData] = useState<any[]>([]);
  const [loadingHistoryData, setLoadingHistoryData] = useState(false);
  const [expandedHistoryRows, setExpandedHistoryRows] = useState<string[]>([]);
  const [historyParentPage, setHistoryParentPage] = useState(0);
  const [historyParentPageSize, setHistoryParentPageSize] = useState(5);

  // Fetch patient history on mount
  useEffect(() => {
    if (rowID) {
      const pid = parseInt(rowID as any, 10);
      if (!Number.isNaN(pid)) {
        (async () => {
          try {
            setLoadingHistoryData(true);
            const resp = await requestService.getPatientProcessHistory({ ptId: pid });
            if (resp && Array.isArray(resp.data)) setHistoryApiData(resp.data);
          } catch (e) {
            console.error("Error loading patient history:", e);
            setHistoryApiData([]);
          } finally {
            setLoadingHistoryData(false);
          }
        })();
      }
    }
  }, [rowID]);

  // Transform patient history API to grid tree
  const transformHistoryData = (apiData: any[]) => {
    const grouped: Record<string, { category: string; date: string; items: any[] }> = apiData.reduce((acc: Record<string, { category: string; date: string; items: any[] }>, item: any) => {
      const category = item.sc_name || "Diğer";
      (item.dates || []).forEach((date: string) => {
        const formatted = new Date(date).toLocaleDateString("tr-TR");
        const key = `${category}_${formatted}`;
        if (!acc[key]) acc[key] = { category, date: formatted, items: [] };
        acc[key].items.push(item);
      });
      return acc;
    }, {} as Record<string, { category: string; date: string; items: any[] }>);

    let parentId = 1;
    return Object.values(grouped).map((group: { category: string; date: string; items: any[] }) => {
      const pid = parentId++;
      return {
        id: pid,
        requestDate: group.date,
        operationName: `${group.category} - ${group.date}`,
        quantity: "",
        note: "",
        isParent: true,
        children: group.items.map((item: any, idx: number) => ({
          id: pid * 100 + idx + 1,
          requestDate: group.date,
          operationName: item.process_name,
          quantity: "1",
          note: item.note || "",
          isParent: false,
          originalData: item,
        })),
      };
    });
  };

  const historyData = React.useMemo(() => {
    let data: any[] = [];
    if (historyApiData.length > 0) data = transformHistoryData(historyApiData);
    
    if (!searchText) return data;

    const lower = searchText.toLocaleLowerCase("tr-TR");
    
    return data.reduce((acc: any[], parent: any) => {
      const parentMatch = parent.operationName.toLocaleLowerCase("tr-TR").includes(lower);
      const matchingChildren = parent.children.filter((child: any) => 
          child.operationName.toLocaleLowerCase("tr-TR").includes(lower)
      );
      
      if (parentMatch) {
         // Keep parent and all children
         acc.push(parent);
      } else if (matchingChildren.length > 0) {
         // Keep parent with filtered children
         acc.push({ ...parent, children: matchingChildren });
      }
      return acc;
    }, []);
  }, [historyApiData, searchText]);

  // Auto-expand rows when searching
  useEffect(() => {
    if (searchText && historyData.length > 0) {
      setExpandedHistoryRows(prev => {
        const newIds = historyData.map(h => h.id.toString());
        const combined = new Set([...prev, ...newIds]);
        return Array.from(combined);
      });
    }
  }, [historyData, searchText]);

  // Flattened history data for grid with pagination
  const flattenedHistoryData = React.useMemo(() => {
    const result: any[] = [];

    // Slice for pagination
    const startIndex = historyParentPage * historyParentPageSize;
    const endIndex = startIndex + historyParentPageSize;
    const paginatedParents = historyData.slice(startIndex, endIndex);

    paginatedParents.forEach(parent => {
      result.push(parent);
      if (expandedHistoryRows.includes(parent.id.toString())) {
        parent.children?.forEach((child: any) => {
          result.push(child);
        });
      }
    });

    return result;
  }, [historyData, expandedHistoryRows, historyParentPage, historyParentPageSize]);

  const handleHistoryParentToggle = (parentId: number | string) => {
    if (onHistoryParentToggle) {
        onHistoryParentToggle(parentId, historyData.find(h => h.id === Number(parentId))?.children);
        return;
    }

    const parent = historyData.find(h => h.id === Number(parentId));
    if (!parent) return;

    const childIds = parent.children?.map((child: any) => child.id) || [];
    const allChildrenSelected = childIds.every((id: any) => selectedHistoryServices.includes(id));

    if (allChildrenSelected) {
      // Deselect all children using toggle
      childIds.forEach((id: any) => {
          // Check if currently selected before toggling off
          if (selectedHistoryServices.includes(id)) {
            const child = parent.children?.find((c: any) => c.id === id);
            onHistoryServiceToggle(id, child?.originalData, id);
          }
      });
    } else {
      // Select unselected children using toggle
      childIds.forEach((id: any) => {
        if (!selectedHistoryServices.includes(id)) {
            const child = parent.children?.find((c: any) => c.id === id);
            onHistoryServiceToggle(id, child?.originalData, id);
        }
      });
    }
  };

  const handleParentToggle = (parentId: string) => {
    setExpandedHistoryRows(prev =>
      prev.includes(parentId)
        ? prev.filter(id => id !== parentId)
        : [...prev, parentId]
    );
  };

  const handleHistoryParentPageChange = (event: unknown, newPage: number) => {
    setHistoryParentPage(newPage);
  };

  const handleHistoryParentRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHistoryParentPageSize(parseInt(event.target.value, 10));
    setHistoryParentPage(0);
  };

  return (
    <HistoryGrid
      rows={flattenedHistoryData}
      selectedHistoryServices={selectedHistoryServices}
      expandedHistoryRows={expandedHistoryRows}
      historyParentPage={historyParentPage}
      historyParentPageSize={historyParentPageSize}
      totalParentCount={historyData.length}
      loading={loadingHistoryData}
      onHistoryServiceToggle={onHistoryServiceToggle}
      onHistoryParentToggle={handleHistoryParentToggle}
      onParentToggle={handleParentToggle}
      onHistoryParentPageChange={handleHistoryParentPageChange}
      onHistoryParentRowsPerPageChange={handleHistoryParentRowsPerPageChange}
    />
  );
};

export default HistoryArea;
